import { Atom } from "@effect-atom/atom";
import { Effect } from "effect";
import { runtime } from "../services/runtime";
import { ApiClient } from "../services/ApiClient";
import type { FiddleInfo, Engine } from "../lib/schemas";
import { xmlContentAtom, xslContentAtom, resultContentAtom, activeResultTabAtom, lastSavedXmlAtom, lastSavedXslAtom } from "./editor";
import { selectedEngineAtom, showHtmlTabAtom, showPdfTabAtom, showConsoleAtom, pdfContentAtom, consoleMessagesAtom, apiConnectedAtom } from "./ui";

// Current fiddle info
export const currentFiddleAtom = Atom.make<FiddleInfo | null>(null);

// Load fiddle from API
export const loadFiddleAtom = runtime.fn<{ id: string; revision?: number }>()(
  Effect.fnUntraced(function* ({ id, revision }, ctx) {
    const client = yield* ApiClient;

    const response = yield* Effect.catchAll(
      revision !== undefined
        ? client.fiddle.loadRevision({ path: { id, revision } })
        : client.fiddle.load({ path: { id } }),
      (error) => {
        console.error("Failed to load fiddle:", error);
        return Effect.succeed({ success: false as const, error: String(error) });
      }
    );

    ctx.set(apiConnectedAtom, response.success);

    if (response.success && response.fiddle) {
      const fiddle = response.fiddle;

      // Update editor content
      if (fiddle.xml) {
        ctx.set(xmlContentAtom, fiddle.xml);
        ctx.set(lastSavedXmlAtom, fiddle.xml);
      }
      if (fiddle.xsl) {
        ctx.set(xslContentAtom, fiddle.xsl);
        ctx.set(lastSavedXslAtom, fiddle.xsl);
      }
      ctx.set(resultContentAtom, fiddle.result ?? "");

      // Update engine
      if (fiddle.engine) {
        ctx.set(selectedEngineAtom, fiddle.engine as Engine);
      }

      // Update fiddle info
      ctx.set(currentFiddleAtom, {
        id: fiddle.shortId ?? id,
        revision: fiddle.revision ?? 1,
      });

      // Check if result is HTML or XSL-FO
      const result = fiddle.result ?? "";
      const isHtml = result.includes("<html") || result.includes("<HTML");
      const isFo = result.includes("http://www.w3.org/1999/XSL/Format");
      ctx.set(showHtmlTabAtom, isHtml);
      ctx.set(showPdfTabAtom, isFo);
    }

    return response;
  })
);

// Load default content
export const loadDefaultsAtom = runtime.fn()(
  Effect.fnUntraced(function* (_, ctx) {
    const client = yield* ApiClient;
    const response = yield* Effect.catchAll(
      client.defaults.load({}),
      () => Effect.succeed({ success: false as const, xml: "", xsl: "" })
    );

    ctx.set(apiConnectedAtom, response.success);

    if (response.success) {
      ctx.set(xmlContentAtom, response.xml);
      ctx.set(xslContentAtom, response.xsl);
      ctx.set(resultContentAtom, "");
      ctx.set(currentFiddleAtom, null);
      ctx.set(showHtmlTabAtom, false);
      ctx.set(showPdfTabAtom, false);
      ctx.set(showConsoleAtom, false);
      ctx.set(pdfContentAtom, null);
      ctx.set(consoleMessagesAtom, []);
      ctx.set(activeResultTabAtom, "result");
    }

    return response;
  })
);
